import {DataTestAttrs, DataTestProps} from '@benchling/js-test-utils';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {HTMLAttributes, ReactNode, useCallback} from 'react';

import {assertAndContinue} from 'Asserts';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import hoverAndActiveStyles from 'styles/hoverAndActiveStyles';
import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import ButtonGroupContext from 'ui-library/buttons/ButtonGroupContext';
import {BLUE, BLUE_LIGHT, GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import pick from 'util/pick';
import {ElementMouseEvent} from 'util/Types';

/*
 * These enums map to styles and sizes in `less/components/buttons.less`.
 */

export enum ButtonSize {
  MINI = 'mini',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum ButtonStyle {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SECONDARY_WHITE = 'secondaryWhite',
  SECONDARY_BLACK = 'secondaryBlack',
  TERTIARY_BLUE = 'tertiaryBlue',
  TERTIARY_BLUE_POWDER = 'tertiaryBluePowder',
  TERTIARY_GRAY = 'tertiaryGray',
  TERTIARY_WHITE = 'tertiaryWhite',
  TERTIARY_WHITE_TRANSPARENT = 'tertiaryWhiteTransparent',
  SUCCESS = 'success',
  NEGATIVE = 'negative',
  WARNING = 'warning',
  WARNING_LIGHT = 'warningLight',
  WARNING_LIGHTEST = 'warningLightest',
  CIRCLE_ICON = 'circleIcon',
  UNBUTTON = 'unButton',
  /** A text-only button best used for anchor tag replacements */
  LINK = 'link',
  /** A text-only button that still has a block-like structure, essentially a button without a background */
  TEXT = 'text',
  /** A background-less, borderless button */
  TRANSPARENT = 'transparent',
}

interface ButtonElementProps extends DataTestProps {
  className: string;
  style: StyleDeclaration;
  'data-toggle': 'dropdown' | null;
  id?: string;
}

function validateButtonStyleAndSize(buttonStyle: ButtonStyle, buttonSize?: ButtonSize | null): void {
  switch (buttonStyle) {
    case ButtonStyle.UNBUTTON:
    case ButtonStyle.LINK:
      assertAndContinue(buttonSize == null, `Expected button size to be null for ${buttonStyle} button.`);
      break;
    default:
      assertAndContinue(buttonSize != null, `Expected button size to not be null for ${buttonStyle} button.`);
  }
}

function _elementProps(componentProps: ButtonProps | ButtonLinkProps): ButtonElementProps {
  validateButtonStyleAndSize(componentProps.buttonStyle, componentProps.size);
  return {
    className: classNames(
      'button',
      // Add the button style
      `button--${componentProps.buttonStyle}`,
      componentProps.buttonStyle === ButtonStyle.TEXT &&
        css(styles.textButton, componentProps.disabled && styles.textButtonDisabled),
      {
        [`button--${componentProps.size}${componentProps.growToFitContents ? '-min-height' : ''}`]: Boolean(
          componentProps.size
        ),
        // ... and extra classes to style icons
        'button--iconText': Boolean(componentProps.icon && componentProps.text),
        'button--icon': Boolean(
          componentProps.icon &&
          !componentProps.text &&
          componentProps.buttonStyle !== ButtonStyle.CIRCLE_ICON
        ),
        [css(styles.transparentButton)]: componentProps.buttonStyle === ButtonStyle.TRANSPARENT,
        // TODO(T7108): We should keep Dropdown state in a component rather than
        // relying on Bootstrap.
        // ... and Bootstrap dropdown classes if it's a dropdown
        'dropdown-toggle': componentProps.isDropdown,
        // By default, a dropdown steals focus from document.activeElement when clicked. In cases when we
        // do not want to steal focus (e.g. if the activeElement has text selection and the dropdown
        // is used to style the selected text), preventDropdownFocus can be set to true.
        'dropdown-toggle--noFocus': componentProps.preventDropdownFocus,
        'button--autoHeight': componentProps.autoHeight,
        'button--fullWidth': componentProps.fullWidth,
        // Fix styling on link-buttons
        // eslint-disable-next-line benchlint/no-in-operator
        'u-display-inline-block': 'href' in componentProps ? Boolean(componentProps.href) : undefined,
        'is-disabled': componentProps.disabled,
        [css(styles.flexWidthButton)]: componentProps.flexWidth,
      },
      // ...and optional extra classes.
      componentProps.className
    ),
    'data-toggle': componentProps.isDropdown && !componentProps.disabled ? 'dropdown' : null,
    id: componentProps.id,
    style: {color: componentProps.textColor, fontWeight: componentProps.fontWeight},
    ...getTestProps(componentProps.dataTestAttrs),
    // Include any additional <button> attributes (i.e. disabled, onClick).
  };
}

interface ButtonWrapperProps {
  alignContentLeft?: boolean;
  buttonStyle: string;
  buttonTextRef?: React.RefObject<HTMLSpanElement>;
  hideDropdownCaret?: boolean;
  icon?: BenchlingIconName | JSX.Element;
  iconAltText?: string;
  isDropdown?: boolean;
  text?: ReactNode;
  textClassName?: string;
}

function ButtonWrapper(props: ButtonWrapperProps): JSX.Element {
  return (
    <div
      className={classNames('button-wrapper', {
        'button-circleIconWrapper': props.buttonStyle === ButtonStyle.CIRCLE_ICON,
        [css(styles.justifySpaceBetween)]:
          !props.hideDropdownCaret && props.isDropdown && props.buttonStyle !== ButtonStyle.CIRCLE_ICON,
        [css(styles.alignContentLeft)]: props.alignContentLeft,
      })}
    >
      {props.icon != null &&
        (typeof props.icon === 'string' ? (
          <BenchlingIcon icon={props.icon} altText={props.iconAltText} />
        ) : (
          props.icon
        ))}
      <span className={classNames(props.textClassName)} ref={props.buttonTextRef}>
        {props.text}
      </span>
      {(props.icon != null || props.text != null) && props.isDropdown && (
        <div className={css(styles.hSpacing)} />
      )}
      {!props.hideDropdownCaret && props.isDropdown && props.buttonStyle !== ButtonStyle.CIRCLE_ICON && (
        <BenchlingIcon icon="chevron-down" altText="Show dropdown menu" />
      )}
    </div>
  );
}

/*
 * Load /_design/buttons to view all the button types, styles, sizes, and other options.
 */

export interface ButtonProps {
  'aria-expanded'?: HTMLAttributes<HTMLButtonElement>['aria-expanded'];
  'aria-label'?: HTMLAttributes<HTMLButtonElement>['aria-label'];
  'aria-pressed'?: HTMLAttributes<HTMLButtonElement>['aria-pressed'];
  alignContentLeft?: boolean;
  autoHeight?: boolean; // sets height: auto on the button element
  /**
   * Button style (i.e. primary, secondary, tertiary, etc.). See ButtonStyle for all options.
   */
  buttonStyle: ButtonStyle;
  buttonTextRef?: React.RefObject<HTMLSpanElement>;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fontWeight?: number;
  fullWidth?: boolean;
  flexWidth?: boolean;
  hideDropdownCaret?: boolean;
  icon?: BenchlingIconName | JSX.Element;
  iconAltText?: string;
  id?: string;
  isDropdown?: boolean;
  preventDropdownFocus?: boolean;
  onClick?: (e: ElementMouseEvent) => void;
  onMouseOut?: (e: ElementMouseEvent) => void;
  onMouseOver?: (e: ElementMouseEvent) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
  text?: ReactNode;
  textColor?: string;
  /**
   * Button size (i.e. mini, small, medium, large). See ButtonSize for all options.
   */
  size?: ButtonSize;
  /** Sets height: auto and min-height to the button size's standard height. This allows buttons to fit
   * their contents & also still have the standardized min size. */
  growToFitContents?: boolean;
  textClassName?: string;
  type?: 'submit' | 'button'; // The HTML spec allows type=reset, but we specifically do not!
  dataTestAttrs?: DataTestAttrs;
}

export default function Button({
  fullWidth = false,
  hideDropdownCaret = false,
  isDropdown = false,
  onMouseDown,
  preventDropdownFocus,
  type = 'button',
  ...otherProps
}: ButtonProps): JSX.Element {
  const _handleMouseDown = useCallback(
    (e: ElementMouseEvent): void => {
      if (onMouseDown) {
        onMouseDown(e);
      }
      if (preventDropdownFocus) {
        e.preventDefault();
      }
    },
    [onMouseDown, preventDropdownFocus]
  );

  // More attribute props can be added as needed. Exclude disabled and handle styling with className, since
  // it prohibits tooltips from showing up.
  const resolvedProps = {
    ...otherProps,
    fullWidth,
    hideDropdownCaret,
    isDropdown,
    type,
  };
  const buttonHTMLProps = pick(
    resolvedProps,
    'aria-expanded',
    'aria-label',
    'aria-pressed',
    'onMouseOut',
    'onMouseOver',
    'type'
  );
  const buttonWrapperProps = pick(
    resolvedProps,
    'buttonStyle',
    'buttonTextRef',
    'hideDropdownCaret',
    'icon',
    'iconAltText',
    'isDropdown',
    'text',
    'textClassName',
    'alignContentLeft'
  );
  return (
    <ButtonGroupContext.Consumer>
      {({inFullWidthButtonGroup}) => (
        // eslint-disable-next-line react/button-has-type
        <button
          {..._elementProps({flexWidth: inFullWidthButtonGroup, ...resolvedProps})}
          {...buttonHTMLProps}
          onMouseDown={_handleMouseDown}
          // Omit onClick if button is disabled so it cannot be triggered; this can alternatively be
          // accomplished with `pointer-events: none;` in CSS, but this additionally disallows other
          // mouse events (e.g. hover).
          onClick={resolvedProps.disabled ? undefined : resolvedProps.onClick}
          {...getTestProps(resolvedProps.dataTestAttrs)}
        >
          <ButtonWrapper {...buttonWrapperProps} />
          {otherProps.children}
        </button>
      )}
    </ButtonGroupContext.Consumer>
  );
}
Button.propTypes = {
  // buttonStyle specifies button style (i.e. primary, see ButtonStyle), named to avoid collisions
  // with HTML attributes.
  buttonStyle: (props: ButtonProps, ...args: [string, string, string, string]) => {
    const styleError = PropTypes.oneOf(Object.values(ButtonStyle))(props, ...args);
    if (styleError) {
      return styleError;
    }
    const {buttonStyle, text} = props;
    if (buttonStyle === ButtonStyle.CIRCLE_ICON && text != null) {
      return new Error(`Buttons with ${buttonStyle} style should not have text (icon-only styles).`);
    }
    return null;
  },
  buttonTextRef: PropTypes.object,
  // Add optional nodes, such as Pulses.
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  dataTestAttrs: DataTestAttrsType,
  fullWidth: PropTypes.bool,
  hideDropdownCaret: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconAltText: PropTypes.string,
  // isDropdown specifies whether the component is a dropdown, and renders dropdown caret if true.
  isDropdown: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseDown: PropTypes.func,
  // size specifies button size (mini, small, medium, large).
  size: PropTypes.oneOf(Object.values(ButtonSize)),
  // text is rendered as text in the button.
  text: (props: ButtonProps, ...args: [string, string, string, string]) => {
    const nodeError = PropTypes.node(props, ...args);
    if (nodeError) {
      return nodeError;
    }
    const {text, icon, isDropdown, children} = props;
    if (!text && !icon && !isDropdown && !children) {
      return new Error('Button should have one of text, icon, children, or isDropdown prop defined.');
    }
    return null;
  },
  textClassName: PropTypes.string,
  type: PropTypes.oneOf(['submit', 'button']),
};

/*
 * A Button-style link component that has the same styling as Button but renders a `<a>` element
 * with support for link attributes (i.e. `href`).
 */

interface ButtonLinkProps extends Omit<ButtonProps, 'type'> {
  href?: string;
  target?: string;
  rel?: string;
  dataTestAttrs?: DataTestAttrs;
}

export function ButtonLink({
  fullWidth = false,
  hideDropdownCaret = false,
  isDropdown = false,
  ...props
}: ButtonLinkProps): JSX.Element {
  const resolvedProps = {...props, fullWidth, hideDropdownCaret, isDropdown};
  const linkHTMLProps = pick(resolvedProps, 'href', 'target', 'rel');
  const buttonWrapperProps = pick(
    resolvedProps,
    'buttonStyle',
    'buttonTextRef',
    'hideDropdownCaret',
    'icon',
    'iconAltText',
    'isDropdown',
    'text',
    'textClassName'
  );
  return (
    <a
      {..._elementProps(resolvedProps)}
      {...linkHTMLProps}
      onClick={resolvedProps.disabled ? undefined : resolvedProps.onClick}
      {...getTestProps(resolvedProps.dataTestAttrs)}
    >
      <ButtonWrapper {...buttonWrapperProps} />
      {resolvedProps.children}
    </a>
  );
}

ButtonLink.propTypes = {
  ...Button.propTypes,
  href: PropTypes.string,
  target: PropTypes.string,
};

const styles = StyleSheet.create({
  flexWidthButton: {
    flex: '1 1 0',
  },
  hSpacing: {
    width: '4px',
  },
  justifySpaceBetween: {
    justifyContent: 'space-between',
  },
  textButton: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: BLUE_LIGHT,
    backgroundColor: 'transparent',
    ...hoverAndActiveStyles({
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: BLUE,
    }),
  },
  textButtonDisabled: {
    color: GRAY_DARK,
    ...hoverAndActiveStyles({
      color: GRAY_DARK,
    }),
  },
  transparentButton: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: 'transparent',
    ...hoverAndActiveStyles({
      backgroundColor: 'transparent',
      boxShadow: 'none',
    }),
  },
  alignContentLeft: {
    justifyContent: 'flex-start',
  },
});

Object.freeze(Button);
